import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useRpcData } from "@redotech/react-util/rpc-data";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import {
  LocationId,
  PackingTemplateId,
  ProductId,
} from "@redotech/redo-ids-cockroach";
import type { Weight } from "@redotech/redo-inventory-common/attributes";
import type { PackingTemplate } from "@redotech/redo-model/inventory/packing-template";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { DescriptionText } from "@redotech/redo-web/arbiter-components/common/descriptors";
import {
  BadgeRedoListItem,
  CustomRedoListItem,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { UserImage, UserImageSize } from "@redotech/redo-web/user-image";
import { memo, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { RedoInventoryRpcClientContext } from "../inventory-rpc-client-provider";

export interface SelectProductInfo {
  id: ProductId;
  isCustom?: boolean;
  title: string;
  sku: string | null;
  upc?: string | null;
  imageUrl?: string | null;
  quantity?: bigint | null;
  barcodes?: string[];
  accountId?: string;
  accountName?: string;
  weight?: Weight | null;
  defaultPackingTemplateId?: PackingTemplateId | null;
  packingTemplates?: PackingTemplate[];
  productFamilyId?: string | null;
  productFamilyTitle?: string | null;
  optionsSortKey?: number[];
}

export interface SelectCustomProductInfo {
  id?: undefined;
  isCustom: true;
  title: string;
  sku: string | null;
}

export type SelectProductInfoOrCustom =
  | SelectProductInfo
  | SelectCustomProductInfo;

interface ProductSelectorPropsBase {
  label?: string;
  placeholder?: string;
  multiSelect: boolean;
  disabled?: boolean;
  errorText?: string;
  locationId?: LocationId;
  buttonOnly?: boolean;
  filterByStock?: boolean;
  excludedProductIds?: ProductId[];
}

export type ProductSelectorProps =
  | (ProductSelectorPropsBase & {
      allowCustom?: false;
      selectedProducts: SelectProductInfo[];
      onSelectionChange: (products: SelectProductInfo[]) => void;
    })
  | (ProductSelectorPropsBase & {
      allowCustom: true;
      selectedProducts: SelectProductInfoOrCustom[];
      onSelectionChange: (products: SelectProductInfoOrCustom[]) => void;
    });

function productKey(product: SelectProductInfoOrCustom): string {
  return product.id ?? product.title;
}

export const ProductSelector = memo(function ProductSelector(
  props: ProductSelectorProps,
) {
  const {
    label,
    placeholder = "Search products...",
    multiSelect,
    disabled = false,
    errorText,
    locationId,
    buttonOnly = false,
    filterByStock = false,
    excludedProductIds,
    allowCustom = false,
  } = props;

  // Widen internally — safe because custom items only appear when allowCustom is true,
  // so callers without allowCustom will only ever receive SelectProductInfo[].
  const selectedProducts =
    props.selectedProducts as SelectProductInfoOrCustom[];
  const onSelectionChange = props.onSelectionChange as (
    products: SelectProductInfoOrCustom[],
  ) => void;

  const inventoryClient = useRequiredContext(RedoInventoryRpcClientContext);

  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const debouncedSearchQuery = useDebounce(searchQuery, 500).trim();

  const searchProductsResult = useRpcData(inventoryClient, "searchProducts", {
    input: {
      query: debouncedSearchQuery,
      locationId,
      limit: 50,
      filterByStock,
    },
    cache: true,
  });

  const excludedProductIdSet = useMemo(
    () => new Set(excludedProductIds ?? []),
    [excludedProductIds],
  );

  const productOptions: RedoListItem<SelectProductInfoOrCustom>[] =
    useMemo(() => {
      const options: RedoListItem<SelectProductInfoOrCustom>[] = (
        searchProductsResult.value?.products ?? []
      )
        .filter((product) => !excludedProductIdSet.has(product.id))
        .map(
          (product): CustomRedoListItem<SelectProductInfoOrCustom> => ({
            id: product.id,
            value: {
              id: product.id,
              title: product.title,
              sku: product.sku,
              upc: product.upc,
              imageUrl: product.imageUrl,
              quantity: product.currentQuantity,
              barcodes: product.barcodes,
              accountId: product.accountId,
              accountName: product.accountName,
              weight: product.weight,
              defaultPackingTemplateId: product.defaultPackingTemplateId,
              packingTemplates: product.packingTemplates,
              productFamilyId: product.productFamilyId,
              productFamilyTitle: product.productFamilyTitle,
              optionsSortKey: product.optionsSortKey,
            },
            type: "custom" as const,
            useWrapper: true,
            render: <ProductRow product={product} />,
          }),
        );

      const trimmedQuery = searchQuery.trim();
      if (allowCustom && trimmedQuery.length > 0) {
        const customItem: SelectCustomProductInfo = {
          isCustom: true,
          title: trimmedQuery,
          sku: null,
        };
        options.push({
          id: "__custom_option__",
          value: customItem,
          type: "custom" as const,
          useWrapper: true,
          render: <CustomProductOption name={trimmedQuery} />,
        });
      }

      return options;
    }, [
      searchProductsResult.value,
      excludedProductIdSet,
      allowCustom,
      searchQuery,
    ]);

  const selectedListItems: RedoListItem<SelectProductInfoOrCustom>[] = useMemo(
    () =>
      selectedProducts.map(
        (product): CustomRedoListItem<SelectProductInfoOrCustom> => ({
          id: productKey(product),
          type: "custom",
          value: product,
          render: <ProductRow product={product} />,
          useWrapper: true,
        }),
      ),
    [selectedProducts],
  );

  const selectedProductBadges: BadgeRedoListItem<SelectProductInfoOrCustom>[] =
    useMemo(
      () =>
        selectedProducts.map(
          (product): BadgeRedoListItem<SelectProductInfoOrCustom> => ({
            id: productKey(product),
            type: "badge",
            value: product,
            badge: {
              avatar: {
                imageUrl: product.id ? (product.imageUrl ?? null) : null,
                alt: product.title,
              },
              text: product.title,
            },
          }),
        ),
      [selectedProducts],
    );

  const handleSelectionChange = useHandler(
    (selectedItems: RedoListItem<SelectProductInfoOrCustom>[]) => {
      onSelectionChange(selectedItems.map((item) => item.value));
    },
  );

  const handleRemoveProduct = useHandler((key: string) => {
    onSelectionChange(selectedProducts.filter((p) => productKey(p) !== key));
  });

  if (multiSelect) {
    return (
      <>
        {buttonOnly ? (
          <RedoButton
            disabled={disabled}
            hierarchy="secondary"
            IconTrailing={ChevronDownIcon}
            onClick={(e) => {
              e.stopPropagation();
              setDropdownOpen(!dropdownOpen);
            }}
            ref={setDropdownAnchor}
            size="xs"
            text={placeholder}
          />
        ) : (
          <Flex dir="column" gap="sm">
            <Text fontSize="xs" fontWeight="regular">
              {label}
            </Text>

            <RedoTagsDropdownInputButton
              disabled={disabled}
              isDropdownOpen={dropdownOpen}
              onClick={(e) => {
                e.stopPropagation();
                setDropdownOpen(!dropdownOpen);
              }}
              placeholder={placeholder}
              ref={setDropdownAnchor}
              removeItem={(item) => {
                handleRemoveProduct(productKey(item.value));
              }}
              selectedItems={selectedProductBadges}
              size="xs"
              state={errorText ? "error" : "default"}
            />
            {errorText ? (
              <DescriptionText
                description={errorText}
                error={!!errorText}
                size="xs"
              />
            ) : null}
          </Flex>
        )}

        <RedoMultiselectDropdown
          dropdownAnchor={dropdownAnchor}
          dropdownOpen={dropdownOpen}
          emptyListMessage={
            productOptions.length === 0 && selectedProducts.length > 0
              ? "All available products are already selected"
              : debouncedSearchQuery.length > 0
                ? "No products found"
                : "Type to search for products"
          }
          fitToAnchor={!buttonOnly}
          items={productOptions}
          itemsLoading={searchProductsResult.pending}
          searchPlaceholder={placeholder}
          searchString={searchQuery}
          selectedItems={selectedListItems}
          selectionVariant="checkmark"
          setDropdownOpen={setDropdownOpen}
          setSearchString={setSearchQuery}
          setSelectedItems={handleSelectionChange}
          size="xs"
        />
      </>
    );
  }

  if (buttonOnly) {
    return (
      <>
        <RedoButton
          disabled={disabled}
          hierarchy="secondary"
          IconTrailing={ChevronDownIcon}
          onClick={(e) => {
            e.stopPropagation();
            setDropdownOpen(!dropdownOpen);
          }}
          ref={setDropdownAnchor}
          size="xs"
          text={placeholder}
        />
        <RedoListDropdown
          dropdownAnchor={dropdownAnchor}
          dropdownOpen={dropdownOpen}
          emptyListMessage={
            debouncedSearchQuery.length > 0
              ? "No products found"
              : "Type to search for products"
          }
          fitToAnchor={false}
          items={productOptions}
          itemSelected={({ item }) => {
            handleSelectionChange([item]);
            setDropdownOpen(false);
          }}
          itemsLoading={searchProductsResult.pending}
          searchPlaceholder={placeholder}
          searchString={searchQuery}
          setDropdownOpen={setDropdownOpen}
          setSearchString={setSearchQuery}
          size="xs"
        />
      </>
    );
  }

  const selectedItem = selectedListItems[0];
  return (
    <RedoSingleSelectDropdownInput
      itemsLoading={searchProductsResult.pending}
      options={productOptions}
      optionSelected={({ item }) => {
        handleSelectionChange([item]);
      }}
      searchPlaceholder={placeholder}
      searchString={searchQuery}
      setSearchString={setSearchQuery}
      size="xs"
      {...(selectedItem && { selectedItem })}
    />
  );
});

const CustomProductOption = memo(function CustomProductOption({
  name,
}: {
  name: string;
}) {
  return <Text fontSize="xs">Use custom product: &ldquo;{name}&rdquo;</Text>;
});

export const ProductRow = memo(function ProductRow({
  product,
  handleRemove,
}: {
  product: SelectProductInfoOrCustom;
  handleRemove?: (key: string) => void;
}) {
  return (
    <Flex align="center" dir="row" gap="sm" w="full">
      {!product.isCustom && (
        <UserImage
          alt={product.title}
          imageUrl={product.imageUrl}
          size={UserImageSize.TINY}
        />
      )}
      <Flex align="flex-start" dir="column" grow={1}>
        <Text fontSize="xs">
          {product.title}
          {product.sku ? (
            <Text as="span" fontSize="xs" fontWeight="thin">
              - {product.sku}
            </Text>
          ) : null}
        </Text>
      </Flex>
      {handleRemove ? (
        <RedoButton
          IconLeading={TrashIcon}
          onClick={() => handleRemove(productKey(product))}
          size="xs"
        />
      ) : null}
    </Flex>
  );
});
